/**
 * Encouraging-feedback Task 4/5: a subtle, reduced-motion-aware "in tune"
 * celebration — a few token-coloured sparkle particles that bloom outward
 * from an anchor element and fade, or (under `prefers-reduced-motion`) a
 * brief static glow on the anchor itself with no motion at all.
 *
 * Built for Task 4's live-view ✨ moment (a sustained excellent-band note,
 * anchored to the current-swara readout — see `app.ts`'s `onPitch`) and
 * reused as-is by Task 5's results-panel "good take" moment, so the two
 * surfaces share one visual language rather than each rolling their own.
 *
 * Deliberately NOT a component: it owns no state, mounts nothing persistent,
 * and cleans up after itself — call it, forget it. Non-blocking (fire and
 * forget; no returned promise the caller needs to await) and safe to call
 * repeatedly, though callers that can fire on every animation frame (like
 * `onPitch`) should debounce themselves (see `EXCELLENT_SUSTAIN_MS` /
 * `celebratedThisRun` in app.ts) — this module has no opinion on WHEN it's
 * appropriate to celebrate, only HOW.
 */

/** How long the particle burst plays before its DOM is torn down. Matches
 * the `celebrate-pop` keyframes' duration (styles.css) plus a small buffer
 * for the last particle's staggered start delay. */
const BURST_LIFETIME_MS = 1050;
/** How long the reduced-motion static glow (`.celebrate-static`, styles.css)
 * stays applied before it's removed. */
const STATIC_HIGHLIGHT_MS = 500;
/** Default sparkle particle count — enough to read as a burst, few enough to
 * stay "subtle" rather than a firework. */
const DEFAULT_PARTICLE_COUNT = 6;

export interface CelebrateOptions {
  /** Overrides the default particle count. */
  particles?: number;
}

/**
 * Fires a sparkle burst anchored to `anchor`'s current on-screen centre (or,
 * under reduced motion, a brief static highlight applied to `anchor`
 * itself). Safe to call on an element that's temporarily zero-sized/detached
 * (e.g. mid-layout-thrash) — it's a no-op in that case rather than drawing a
 * sparkle burst at the viewport origin.
 */
export function celebrate(anchor: HTMLElement, opts: CelebrateOptions = {}): void {
  if (typeof window === 'undefined' || !anchor.isConnected) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced) {
    anchor.classList.add('celebrate-static');
    window.setTimeout(() => anchor.classList.remove('celebrate-static'), STATIC_HIGHLIGHT_MS);
    return;
  }

  const rect = anchor.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return; // not actually laid out — nothing to anchor to

  const count = opts.particles ?? DEFAULT_PARTICLE_COUNT;
  const layer = document.createElement('div');
  layer.className = 'celebrate-layer';
  layer.setAttribute('aria-hidden', 'true'); // purely decorative — the live readout's aria-live text already carries the "in tune" info
  layer.style.left = `${rect.left + rect.width / 2}px`;
  layer.style.top = `${rect.top + rect.height / 2}px`;

  for (let i = 0; i < count; i++) {
    const particle = document.createElement('span');
    particle.className = 'celebrate-particle';
    particle.textContent = '✨';
    // Evenly spaced around the anchor, with a little jitter so the burst
    // reads as organic rather than a mechanical ring.
    const angle = (i / count) * 2 * Math.PI + (Math.random() - 0.5) * 0.6;
    const dist = 16 + Math.random() * 14;
    particle.style.setProperty('--cx', `${Math.cos(angle) * dist}px`);
    particle.style.setProperty('--cy', `${Math.sin(angle) * dist}px`);
    particle.style.animationDelay = `${Math.random() * 90}ms`;
    layer.appendChild(particle);
  }

  document.body.appendChild(layer);
  window.setTimeout(() => layer.remove(), BURST_LIFETIME_MS);
}
